import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@material-ui/core";
import { AppBarButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { useDispatch, useSelector } from "react-redux";
import { itemSelector } from "../../redux/selectors";
import { Scope } from "../../domain/Scope";
import { Save } from "@material-ui/icons";
import { updatingScopes } from "../../redux/actions";
import {
  buildMetaData,
  getLookupTracker,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { LookupTrackerStore } from "@ofqual/portal-core/core/types";

export const SaveScopesButtonAndDialog: React.FC<{
  orgId: string;
  id: string;
  disabled: boolean;
}> = ({ orgId, id, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { scopes, lookupTrackerStore, cleanScopes } = useSelector(itemSelector);

  return (
    <>
      <AppBarButton
        text="Save"
        tooltip="Save"
        Icon={Save}
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      />
      {isOpen && scopes && (
        <SaveScopeDialog
          scopes={scopes}
          lookupTrackerStore={lookupTrackerStore}
          currentScopes={cleanScopes}
          id={id}
          orgId={orgId}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

type SaveScopesButtonAndDialogProps = {
  id: string;
  orgId: string;
  scopes: Scope[];
  lookupTrackerStore: LookupTrackerStore;
  currentScopes: Scope[];
  onClose: () => void;
};

const SaveScopeDialog: React.FC<SaveScopesButtonAndDialogProps> = ({
  id,
  orgId,
  scopes,
  onClose,
  lookupTrackerStore,
  currentScopes,
}) => {
  const dispatch = useDispatch();

  const { control, handleSubmit, formState } = useForm();
  const lookupTracker = useMemo(() => getLookupTracker(lookupTrackerStore), [
    lookupTrackerStore,
  ]);

  const onSubmit = (data: { message: string }) => {
    const editedScopes = scopes.filter((scope) => scope.dirtyFields);
    const editedScopesWithMeta = editedScopes.map((editedScope) => {
      const currentScope = currentScopes.find(
        (scope) => scope.id === editedScope.id
      );
      const fields = editedScope.dirtyFields as (keyof Scope)[];
      return {
        model: editedScope,
        meta: buildMetaData<Scope>(
          editedScope,
          currentScope!,
          fields,
          lookupTracker
        ),
      };
    });
    dispatch(updatingScopes(orgId, id, editedScopesWithMeta, data.message));
  };

  const hasShareUpdated = useHasReduxObjectUpdated(scopes);

  useEffect(() => {
    if (formState.isSubmitted && hasShareUpdated) {
      onClose();
    }
  }, [hasShareUpdated, formState.isSubmitted, onClose, id]);

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Update scopes</DialogTitle>

        <DialogContent>
          <form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="message"
                  defaultValue={null}
                  as={
                    <TextField
                      label="Note"
                      data-test="scope-decision-to-message"
                      multiline
                      rows={5}
                    />
                  }
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
          <Button onClick={handleSubmit(onSubmit)} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useHasReduxObjectUpdated = (object: any) => {
  const ref = useRef(object);
  return object !== ref.current;
};
